import {
  HStack,
  PanelShell,
  Switch,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import React from 'react';
import { TimeChart } from '../Viz/TimeChart/TimeChart';
import {
  TimeChartConfig,
  TimeChartData,
  TimeChartLegendConfig,
} from '../Viz/TimeChart/types';
import { TimeChartLegend } from '../Viz/TimeChart/TimeChartLegend';
import { Box } from '@chakra-ui/react';
import FlashIconSVG from '../CustomSVGs/FlashIconSVG';
import { PanelWidget } from './PanelWidget';
import { Select } from '../Form/Select';
import { Trend } from '../../models/score';
import { DataRange } from './types';

const DATA_RANGE_OPTS: { label: string; value: DataRange }[] = [
  { label: '1 Month', value: 30 },
  { label: '3 Months', value: 90 },
  { label: 'All Time', value: 0 },
];

const DATA_RANGE_LABEL_MAP: { [key in DataRange]: string } = {
  0: 'since onboarding',
  30: 'over the last month',
  90: 'over the last 3 months',
};

type PanelData = {
  overallSymptoms: { trend: Trend; value: number };
  timeChartData: TimeChartData;
};

type Props = {
  data: PanelData;
  dataRange: DataRange;
  legendConfig: TimeChartLegendConfig;
  memberView: boolean;
  onSelectDataRange: (dataRange: DataRange) => void;
  onToggleLegendItem: (dataKey, visible) => void;
  onToggleMemberView: (state: boolean) => void;
  timeChartConfig: TimeChartConfig;
};

export const Panel = ({
  data,
  dataRange,
  legendConfig,
  memberView,
  onSelectDataRange,
  onToggleLegendItem,
  onToggleMemberView,
  timeChartConfig,
}: Props) => {
  const { overallSymptoms, timeChartData } = data;
  const dataRangeLabel = DATA_RANGE_LABEL_MAP[dataRange];

  return (
    <PanelShell>
      <HStack alignItems={'center'} justifyContent={'space-between'} mb={3}>
        <HStack alignItems={'center'}>
          <FlashIconSVG />
          <Text
            color={'secondary.200'}
            fontSize={'md'}
            fontWeight={'normal'}
            ml={1}
          >
            Symptom Severity
          </Text>
        </HStack>

        <HStack alignItems="center" flex={3} justifyContent={'right'}>
          <Box w={'160px'} mr={6}>
            <Select
              height={38}
              maxWidth={136}
              borderColor={'cloud.200'}
              indicatorSeparatorColor={'transparent'}
              placeholder="Date Range"
              onChange={(opt: { label: string; value: DataRange }) =>
                onSelectDataRange(opt.value)
              }
              options={DATA_RANGE_OPTS as any}
              value={DATA_RANGE_OPTS.find((r) => r.value === dataRange)}
            />
          </Box>

          <Text color={'secondary.600'} fontSize={'xs'}>
            Member view
          </Text>

          <Switch
            isChecked={memberView}
            onChange={onToggleMemberView}
            style={{ marginLeft: 10 }}
          />
        </HStack>
      </HStack>

      <HStack>
        <VStack flex={1}>
          <Box mb={6}>
            <TimeChartLegend
              dataRangeLabel={dataRangeLabel}
              lines={legendConfig.lines}
              onToggleLegendItem={onToggleLegendItem}
              readonly={memberView}
            />
          </Box>

          <TimeChart config={timeChartConfig} data={timeChartData} />
        </VStack>

        <VStack>
          <Box mb={3}>
            <PanelWidget
              badgeColor={'secondary.200'}
              badgeStyle={'solid'}
              circleStyle={memberView ? 'solid' : 'outline'}
              title={'Overall Symptoms'}
              trend={overallSymptoms.trend}
              value={overallSymptoms.value}
            >
              <Text
                color={'blueGray.600'}
                fontSize={'2xs'}
                fontWeight={'normal'}
              >
                {dataRangeLabel}
              </Text>
            </PanelWidget>
          </Box>

          <PanelWidget
            badgeColor={'#FEB117'}
            badgeStyle={'outline'}
            badgeText={'Large Correlation'}
            circleStyle={memberView ? 'solid' : 'outline'}
            title={'Stress & Symptoms'}
            trend={Trend.down}
          >
            <Text color={'blueGray.600'} fontSize={'2xs'} fontWeight={'normal'}>
              <Text fontWeight={'bold'}>r=NA</Text> <br />
              NA
            </Text>
          </PanelWidget>
        </VStack>
      </HStack>
    </PanelShell>
  );
};
